import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as feesService from "../service/feesService";
import type { FeesState, FeeStructureItemInput, PaymentMode, DiscountType, AdhocChargeTargetScope } from "../types";
import i18n from "@/i18n";

export const fetchFeeComponents = createAsyncThunk("fees/fetchComponents", async (institutionId: number) => {
  return await feesService.fetchFeeComponents(institutionId);
});

/** Only fee types opted into the Extra Charges picker — kept in a separate state field from `components` so this page's narrower view never clobbers the structure builder's full list. */
export const fetchAdhocFeeComponents = createAsyncThunk("fees/fetchAdhocComponents", async (institutionId: number) => {
  return await feesService.fetchFeeComponents(institutionId, { scope: "adhoc" });
});

export const createFeeComponent = createAsyncThunk(
  "fees/createComponent",
  async (
    { institutionId, payload }: { institutionId: number; payload: { name: string; facilityTag?: string; description?: string; isAdhocEligible?: boolean } },
    { dispatch }
  ) => {
    await feesService.createFeeComponent(institutionId, payload);
    await dispatch(fetchFeeComponents(institutionId));
    await dispatch(fetchAdhocFeeComponents(institutionId));
  }
);

export const updateFeeComponent = createAsyncThunk(
  "fees/updateComponent",
  async (
    { institutionId, componentId, payload }: { institutionId: number; componentId: number; payload: { name?: string; description?: string; isActive?: boolean; isAdhocEligible?: boolean } },
    { dispatch }
  ) => {
    await feesService.updateFeeComponent(institutionId, componentId, payload);
    await dispatch(fetchFeeComponents(institutionId));
    await dispatch(fetchAdhocFeeComponents(institutionId));
  }
);

export const deleteFeeComponent = createAsyncThunk(
  "fees/deleteComponent",
  async ({ institutionId, componentId }: { institutionId: number; componentId: number }, { dispatch }) => {
    const result = await feesService.deleteFeeComponent(institutionId, componentId);
    await dispatch(fetchFeeComponents(institutionId));
    await dispatch(fetchAdhocFeeComponents(institutionId));
    return result;
  }
);

export const fetchFeeStructures = createAsyncThunk(
  "fees/fetchStructures",
  async ({ institutionId, academicYearId, academicGroupId }: { institutionId: number; academicYearId?: number; academicGroupId?: number }) => {
    return await feesService.fetchFeeStructures(institutionId, { academicYearId, academicGroupId });
  }
);

// Same endpoint as fetchFeeStructures, without an academicGroupId — every
// structure for the year across all groups. Kept as a separate thunk
// (own loading flag not needed, but its own action type) so it can write
// to `allStructures` without clobbering `structures`, which the wizard
// step-flow above scopes to one group at a time.
export const fetchAllFeeStructures = createAsyncThunk(
  "fees/fetchAllStructures",
  async ({ institutionId, academicYearId }: { institutionId: number; academicYearId: number }) => {
    return await feesService.fetchFeeStructures(institutionId, { academicYearId });
  }
);

export const deleteFeeStructure = createAsyncThunk(
  "fees/deleteStructure",
  async ({ institutionId, structureId }: { institutionId: number; structureId: number }) => {
    await feesService.deleteFeeStructure(institutionId, structureId);
    return structureId;
  }
);

export const loadFeeStructure = createAsyncThunk(
  "fees/loadStructure",
  async ({ institutionId, structureId }: { institutionId: number; structureId: number }) => {
    return await feesService.fetchFeeStructure(institutionId, structureId);
  }
);

export const saveFeeStructure = createAsyncThunk(
  "fees/saveStructure",
  async (
    {
      institutionId,
      academicYearId,
      academicGroupId,
      name,
      items,
    }: { institutionId: number; academicYearId: number; academicGroupId: number; name?: string; items: FeeStructureItemInput[] },
  ) => {
    return await feesService.saveFeeStructure(institutionId, { academicYearId, academicGroupId, name, items });
  }
);

export const publishFeeStructure = createAsyncThunk(
  "fees/publishStructure",
  async ({ institutionId, structureId }: { institutionId: number; structureId: number }) => {
    return await feesService.publishFeeStructure(institutionId, structureId);
  }
);

export const generateInvoices = createAsyncThunk(
  "fees/generateInvoices",
  async ({ institutionId, academicGroupId, academicYearId }: { institutionId: number; academicGroupId: number; academicYearId: number }) => {
    return await feesService.generateInvoicesForClass(institutionId, { academicGroupId, academicYearId });
  }
);

export const fetchStudentInvoices = createAsyncThunk(
  "fees/fetchStudentInvoices",
  async ({ institutionId, studentId }: { institutionId: number; studentId: number }) => {
    return await feesService.fetchStudentInvoices(institutionId, studentId);
  }
);

export const loadInvoice = createAsyncThunk(
  "fees/loadInvoice",
  async ({ institutionId, invoiceId }: { institutionId: number; invoiceId: number }) => {
    const invoice = await feesService.fetchInvoice(institutionId, invoiceId);
    const payments = await feesService.fetchPayments(institutionId, invoiceId);
    return { invoice, payments };
  }
);

export const recordPayment = createAsyncThunk(
  "fees/recordPayment",
  async (
    {
      institutionId,
      invoiceId,
      amount,
      paymentMode,
      bankAccountId,
      referenceNo,
      notes,
      invoiceItemIds,
    }: { institutionId: number; invoiceId: number; amount: number; paymentMode?: PaymentMode; bankAccountId?: number; referenceNo?: string; notes?: string; invoiceItemIds?: number[] },
    { dispatch }
  ) => {
    await feesService.recordPayment(institutionId, invoiceId, { amount, paymentMode, bankAccountId, referenceNo, notes, invoiceItemIds });
    return await dispatch(loadInvoice({ institutionId, invoiceId })).unwrap();
  }
);

export const fetchFeesOverview = createAsyncThunk(
  "fees/fetchOverview",
  async ({ institutionId, academicYearId }: { institutionId: number; academicYearId?: number }) => {
    return await feesService.fetchFeesOverview(institutionId, { academicYearId });
  }
);

export const fetchFeesBreakdown = createAsyncThunk(
  "fees/fetchBreakdown",
  async (
    { institutionId, groupBy, academicYearId, from, to }: { institutionId: number; groupBy?: "class" | "date"; academicYearId?: number; from?: string; to?: string },
  ) => {
    return await feesService.fetchFeesBreakdown(institutionId, { groupBy, academicYearId, from, to });
  }
);

export const fetchInvoiceDiscounts = createAsyncThunk(
  "fees/fetchDiscounts",
  async ({ institutionId, invoiceId }: { institutionId: number; invoiceId: number }) => {
    return await feesService.fetchInvoiceDiscounts(institutionId, invoiceId);
  }
);

export const applyInvoiceDiscount = createAsyncThunk(
  "fees/applyDiscount",
  async (
    {
      institutionId,
      invoiceId,
      discountType,
      value,
      invoiceItemId,
      reason,
    }: { institutionId: number; invoiceId: number; discountType: DiscountType; value: number; invoiceItemId?: number; reason?: string },
    { dispatch }
  ) => {
    const result = await feesService.applyInvoiceDiscount(institutionId, invoiceId, { discountType, value, invoiceItemId, reason });
    await dispatch(loadInvoice({ institutionId, invoiceId }));
    return result;
  }
);

export const revokeInvoiceDiscount = createAsyncThunk(
  "fees/revokeDiscount",
  async (
    { institutionId, invoiceId, discountId }: { institutionId: number; invoiceId: number; discountId: number },
    { dispatch }
  ) => {
    const result = await feesService.revokeInvoiceDiscount(institutionId, invoiceId, discountId);
    await dispatch(loadInvoice({ institutionId, invoiceId }));
    return result;
  }
);

export const ADHOC_CHARGES_PAGE_SIZE = 10;

export const fetchAdhocCharges = createAsyncThunk(
  "fees/fetchAdhocCharges",
  async ({ institutionId, academicYearId, page = 1 }: { institutionId: number; academicYearId?: number; page?: number }) => {
    return await feesService.fetchAdhocCharges(institutionId, { academicYearId, page, limit: ADHOC_CHARGES_PAGE_SIZE });
  }
);

export const createAdhocCharge = createAsyncThunk(
  "fees/createAdhocCharge",
  async (
    {
      institutionId,
      payload,
    }: {
      institutionId: number;
      payload: {
        academicYearId: number;
        feeComponentId: number;
        name: string;
        amount: number;
        dueDate?: string;
        targetScope: AdhocChargeTargetScope;
        academicGroupIds?: number[];
        studentIds?: number[];
        note?: string;
      };
    },
    { dispatch }
  ) => {
    const result = await feesService.createAdhocCharge(institutionId, payload);
    await dispatch(fetchAdhocCharges({ institutionId, academicYearId: payload.academicYearId, page: 1 }));
    return result;
  }
);

export const cancelAdhocCharge = createAsyncThunk(
  "fees/cancelAdhocCharge",
  async ({ institutionId, chargeId, academicYearId }: { institutionId: number; chargeId: number; academicYearId?: number }, { dispatch, getState }) => {
    const result = await feesService.cancelAdhocCharge(institutionId, chargeId);
    const page = (getState() as { fees: FeesState }).fees.adhocChargesPagination.page;
    await dispatch(fetchAdhocCharges({ institutionId, academicYearId, page }));
    return result;
  }
);

const initialState: FeesState = {
  components: [],
  adhocComponents: [],
  structures: [],
  allStructures: [],
  activeStructure: null,
  lastAutoAssigned: null,
  studentInvoices: [],
  activeInvoice: null,
  payments: [],
  overview: null,
  breakdown: [],
  discounts: [],
  adhocCharges: [],
  adhocChargesPagination: { page: 1, limit: ADHOC_CHARGES_PAGE_SIZE, total: 0, totalPages: 1 },
  loading: false,
  saving: false,
  error: null,
};

const feesSlice = createSlice({
  name: "fees",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeeComponents.fulfilled, (state, action) => {
        state.components = action.payload;
      })
      .addCase(fetchAdhocFeeComponents.fulfilled, (state, action) => {
        state.adhocComponents = action.payload;
      })
      .addCase(fetchFeeStructures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeeStructures.fulfilled, (state, action) => {
        state.loading = false;
        state.structures = action.payload;
      })
      .addCase(fetchFeeStructures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("fees:feesSlicetsMsgs.failedToLoadFeeStructures");
      })
      .addCase(loadFeeStructure.fulfilled, (state, action) => {
        state.activeStructure = action.payload;
      })
      .addCase(fetchAllFeeStructures.fulfilled, (state, action) => {
        state.allStructures = action.payload;
      })
      .addCase(deleteFeeStructure.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.allStructures = state.allStructures.filter((s) => s.id !== deletedId);
        state.structures = state.structures.filter((s) => s.id !== deletedId);
        if (state.activeStructure?.id === deletedId) state.activeStructure = null;
      })
      .addCase(deleteFeeStructure.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("fees:feesSlicetsMsgs.failedToDeleteFeeStructure");
      })
      .addCase(saveFeeStructure.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveFeeStructure.fulfilled, (state, action) => {
        state.saving = false;
        state.activeStructure = action.payload;
      })
      .addCase(saveFeeStructure.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? i18n.t("fees:feesSlicetsMsgs.failedToSaveFeeStructure");
      })
      .addCase(publishFeeStructure.fulfilled, (state, action) => {
        state.activeStructure = action.payload.structure;
        state.lastAutoAssigned = action.payload.autoAssigned;
      })
      .addCase(generateInvoices.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("fees:feesSlicetsMsgs.failedToGenerateInvoices");
      })
      .addCase(fetchStudentInvoices.fulfilled, (state, action) => {
        state.studentInvoices = action.payload;
      })
      .addCase(loadInvoice.fulfilled, (state, action) => {
        state.activeInvoice = action.payload.invoice;
        state.payments = action.payload.payments;
      })
      .addCase(recordPayment.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("fees:feesSlicetsMsgs.failedToRecordPayment");
      })
      .addCase(fetchFeesOverview.fulfilled, (state, action) => {
        state.overview = action.payload;
      })
      .addCase(fetchFeesBreakdown.fulfilled, (state, action) => {
        state.breakdown = action.payload;
      })
      .addCase(fetchInvoiceDiscounts.fulfilled, (state, action) => {
        state.discounts = action.payload.discounts;
      })
      .addCase(applyInvoiceDiscount.fulfilled, (state, action) => {
        state.discounts = action.payload.discounts;
      })
      .addCase(applyInvoiceDiscount.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("fees:feesSlicetsMsgs.failedToApplyDiscount");
      })
      .addCase(revokeInvoiceDiscount.fulfilled, (state, action) => {
        state.discounts = action.payload.discounts;
      })
      .addCase(revokeInvoiceDiscount.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("fees:feesSlicetsMsgs.failedToRevokeDiscount");
      })
      .addCase(fetchAdhocCharges.fulfilled, (state, action) => {
        state.adhocCharges = action.payload.charges;
        state.adhocChargesPagination = action.payload.pagination;
      })
      .addCase(createAdhocCharge.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("fees:feesSlicetsMsgs.failedToCreateExtraCharge");
      })
      .addCase(cancelAdhocCharge.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("fees:feesSlicetsMsgs.failedToCancelExtraCharge");
      });
  },
});

export default feesSlice.reducer;
